import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ListBox } from 'primereact/listbox';
import GraphlqlQuery from '../../components/GraphlqlQuery/GraphlqlQuery';
import { ProgressSpinner } from 'primereact/progressspinner';
import ResiduesTable from '../../components/ResiduesTable/ResiduesTable';
import FeaturesViewer from '../../components/FeaturesViewer/FeaturesViewer';
import NetworkDiagram from '../../components/NetworkDiagram/NetworkDiagram';
import Legend from '../../components/Legend/Legend';
import { Link } from 'react-router-dom';
import MolstarViewerFunctional from '../../components/MolstarViewerFunctional/MolstarViewerFunctional';
import FunctionalAnnotationStructureLegend from '../../components/FunctionalAnnotationStructureLegend/FunctionalAnnotationStructureLegend';
import { Tooltip } from 'primereact/tooltip';
import { createDomainsColors, getInterProDomains} from '../../ApiFunctions.js';
import { getPDBsFromUniprot } from '../../ApiFunctions';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';


import {downloadSvg, downloadSvgAsPNG} from '../../download.js';

// Memoize the FeaturesViewer component
const MemoizedFeaturesViewer = React.memo(FeaturesViewer);
const optionsModelType = [
    { label: 'Monomer', value: 'Monomer' },
    { label: 'Multimer', value: 'Multimer' }
];

const ResidueSection = ({ datatable, loadFunctionalInfo, type, target, activeTabIndex, residueGeneData, annotationMap }) => {


   // Processing data
   const [uniprotNumber, setUniprotNumber] = useState(null);
   const [pdbList, setPdbList] = useState([]);
   const [structurePDBId, setStructurePDBId] = useState("");
   const [domainInterPro, setDomainInterPro] = useState(null);
   const [valueModelType, setValueModelType] = useState('Monomer');
   const [multimerMap, setMultimerMap] = useState({});
   const [selectedStructureFile, setSelectedStructureFile] = useState(null);

   const svgRef = useRef();

   const annotations = ['gaussian', 'afPredictedSecondaryStructure', 'unSecondaryStructure', 'surface', 'hydrophobicity', 'rainbow', 'name'];

   const { data, colors } = useMemo(() => {
    let processedData = null;
    let domainColors = {};
        if (Array.isArray(datatable) && datatable.length >= 1 && datatable[0] !== undefined) {
            processedData = datatable.map(residue => {
                const { interContacts, intraContacts, ...filteredResidue } = residue;
                return filteredResidue;
            });
            domainColors = createDomainsColors(processedData);
        } else if (Array.isArray(residueGeneData) && residueGeneData.length >= 1 && residueGeneData[0] !== undefined) {
            processedData = residueGeneData.map(residue => {
                const { interContacts, intraContacts, ...filteredResidue } = residue;
                return filteredResidue;
            });
            domainColors = createDomainsColors(processedData);
        }
        return { data: processedData, colors: domainColors };
   }, [datatable, residueGeneData]);

   useEffect(() => {
    const loadData = async () => {
        try {
            const multimerModule = await import('../../mapa_alphafold.json');
            setMultimerMap(multimerModule.default || multimerModule);
        } catch (err) {
            console.error("Failed to load multimer data", err);
        }
    };
    loadData();
}, []);

// Normalize the map entry to an array of structure records.
// Falls back gracefully if an old single-URL string is still present.
const structureOptions = useMemo(() => {
    const entry = uniprotNumber ? multimerMap[uniprotNumber] : null;
    if (!entry) return [];
    if (typeof entry === 'string') {
        return [{ url: entry, file: entry, chain: 'A', partner: null, type: 'multimer' }];
    }
    return Array.isArray(entry) ? entry : [];
}, [uniprotNumber, multimerMap]);

const hasMultimer = structureOptions.length > 0;

// The structure currently chosen in the dropdown (defaults to the first).
const selectedStructure = useMemo(
    () => structureOptions.find(s => s.file === selectedStructureFile) || structureOptions[0] || null,
    [structureOptions, selectedStructureFile]
);

const structureDropdownOptions = useMemo(
    () => structureOptions.map(s => ({
        value: s.file,
        label: s.type === 'homodimer'
            ? 'Homodimer'
            : `Heterodimer with ${s.partner}`,
    })),
    [structureOptions]
);

const multimerLink = (valueModelType === 'Multimer' && selectedStructure) ? selectedStructure.url : null;

// Pick a sensible default (prefer the homodimer) when the options change.
useEffect(() => {
    if (!structureOptions.length) {
        setSelectedStructureFile(null);
        return;
    }
    const preferred = structureOptions.find(s => s.type === 'homodimer') || structureOptions[0];
    setSelectedStructureFile(preferred.file);
}, [structureOptions]);

useEffect(() => {
    if (!hasMultimer && valueModelType === 'Multimer') {
        setValueModelType('Monomer');
    }
}, [hasMultimer, valueModelType]);

   useEffect(() => {
        if (data && data.length > 0) {
            const localNumber = data[0]?.anPosition?.split("_")[0];
            if (localNumber && localNumber !== uniprotNumber) {
                setUniprotNumber(localNumber);
            }
        } else if (!data && uniprotNumber !== null) {
            setUniprotNumber(null);
        }
    }, [data, uniprotNumber]);

   useEffect(() => {
    const fetchPDBs = async () => {
            if (typeof uniprotNumber !== 'string' || !uniprotNumber.trim()) {
                setPdbList([""]);
                setStructurePDBId("");
                return;
            }
            try {
                const result = await getPDBsFromUniprot(uniprotNumber);
                const uniquePdbIds = ["", ...new Set(result || [])];
                setPdbList(uniquePdbIds);
                setStructurePDBId("");
            } catch (error) {
                console.error("Error fetching PDBs:", error);
                setPdbList([""]);
                setStructurePDBId("");
            }
        };

        fetchPDBs();
    }, [uniprotNumber]);

 useEffect(() => {
    if (typeof uniprotNumber !== 'string' || !uniprotNumber.trim()) return;

    const fetchDomains = async () => {
        try {
            const result = await getInterProDomains(uniprotNumber);
            if (!result) return [];

           if (valueModelType === 'Multimer' && selectedStructure) {
            if (selectedStructure.type === 'homodimer') {
                const chainA = result.map(item => ({ ...item, chain: 'A' }));
                const chainB = result.map(item => ({ ...item, chain: 'B' }));
                setDomainInterPro([...chainA, ...chainB]);
                return;
            }
            // heterodimer: annotate only the chain the target protein occupies
            const targetChain = selectedStructure.chain || 'A';
            setDomainInterPro(result.map(item => ({ ...item, chain: targetChain })));
            return;
        }
        const resultWithChain = result.map(item => ({ ...item, chain: 'A' }));
        setDomainInterPro(resultWithChain);

        } catch (error) {
            console.error('Error fetching InterPro domains:', error);
            setDomainInterPro(null);
        }
    };

    fetchDomains();
    }, [uniprotNumber, valueModelType, selectedStructure]);


// Dialog settings
const [visible, setVisible] = useState(false);
const [position, setPosition] = useState('center');

const show = (position) => {
        setPosition(position);
        setVisible(true);
};

// Structure section
const [annotation, setAnnotation] = useState("pLDDT");
const [selectedLigand, setSelectedLigand] = useState(null);
const [ligandsOption, setLigandsOption] = useState([]);
const [superpose, setSuperpose] = useState(false);

const handleAnnotation = (e) => {
    setAnnotation(e.target.value);
    setSuperpose(false);
    setStructurePDBId("");
};

const filterLigandsReset = () => {
    setSelectedLigand([]);
}

const handlePDBid = async (e) => {
    const value = e.target.value;
    setStructurePDBId(value);
    setSuperpose(false);
}

const handleReset = () => {
   setSuperpose(false);
   setStructurePDBId("");
}

const handleExportSVG = () => {
    downloadSvg(svgRef);
};

// Export SVG
const handleExportPNG = () => {
    downloadSvgAsPNG(svgRef);
};


const dataForMolstar = useMemo(() => {

    if (!data) return [];

    if (valueModelType === 'Multimer' && selectedStructure) {
        if (selectedStructure.type === 'homodimer') {
            const chainA = data.map(item => ({ ...item, chain: 'A' }));
            const chainB = data.map(item => ({ ...item, chain: 'B' }));
            return [...chainA, ...chainB];
        }
        // heterodimer: colour only the chain the target protein occupies
        const targetChain = selectedStructure.chain || 'A';
        return data.map(item => ({ ...item, chain: targetChain }));
    }

    return data.map(item => ({ ...item, chain: 'A' }));
}, [data, valueModelType, selectedStructure]);

return (
     <>
     <div className="row mb-4">
            <div className="col-12">
               <h2 className="display-6 fw-bold mb-5">Functional site information<sup><Button icon="pi pi-info"
               onClick={() => show('top-right')} text size="small" className="btn-dialog"/></sup></h2>
               <Dialog header="Functional site information" visible={visible} position={position} style={{ width: '50vw' }} onHide={() => setVisible(false)}
                draggable={false} resizable={false}  breakpoints={{ '960px': '75vw', '641px': '100vw' }}>
                <p className="m-0 mb-1 text-justify">
                    This section provides various types of annotations at the residue level, including UniProt annotations such
                    as variants, molecule processing, membrane localization, motifs, domains, glycosylations, lipidations,
                    modified residues, disulfide bonds, and secondary structures. Protein architecture information is provided
                    through PFAM and InterPro domains and aligned positions in the Hidden Markov Model (HMM). Additionally,
                    it includes information on IntAct binding regions and protein post-translational modifications
                    sourced from PhosphoSitePlus.
                    </p>
                    <p className="m-0 mb-1 text-justify"> The functional annotation for each residue is presented in a table with 23 columns:</p>
                    <ul>
                    <li className="text-justify">The 'Position ' column indicates the residue position in the protein sequence.</li>
                    <li className="text-justify">The 'Ref' column contains the UniProt reference proteome residue.</li>
                    <li className="text-justify">The 'afRes' column contains the predicted AlphaFold residue.</li>
                    <li className="text-justify">The 'Natural Variants' column contains the UniProt annotated natural variants.</li>
                    <li className="text-justify">The 'Molecule Processing' column contains the UniProt annotated molecule processing (signal-peptide/transit-peptide/pro-peptide/chain).</li>
                    <li className="text-justify">The 'Location ' column contains the UniProt localization with respect to membranes.</li>
                    <li className="text-justify">The 'Motives ' column contains the UniProt annotated motives. </li>
                    <li className="text-justify">The 'Domains ' column contains the UniProt annotated domains. </li>
                    <li className="text-justify">The 'PFAM-Domain HMM Pos' column contains the PFAM domain and aligned position in the HMM.</li>
                    <li className="text-justify">The 'Binding Site' column contains the UniProt annotated ligands (binding site).</li>
                    <li className="text-justify">The 'Active Site' column contains the UniProt annotated properties (active site).</li>
                    <li className="text-justify">The 'PTMs' column contains the list of annotated Phosphosite PTMs.</li>
                    <li className="text-justify">The 'Glycosylations' column contains the UniProt annotated glycosylations.</li>
                    <li className="text-justify">The 'Lipidations' column contains the UniProt annotated lipidations.</li>
                    <li className="text-justify">The 'Modified Residues' column contains the UniProt annotated modified residues.</li>
                    <li className="text-justify">The 'Disulfide bong bridges' column contains the UniProt annotated disulphide-bond bridges.</li>
                    <li className="text-justify">The 'Secondary Structure' column contains the UniProt annotated secondary structure.</li>
                    <li className="text-justify">The 'AF-Predicted Secondary Structure' column contains the AlphaFold predicted secondary structure.</li>
                    <li className="text-justify">The 'AF-Confidence Value' column contains the AlphaFold confidence value.</li>
                    <li className="text-justify">The 'CrossLinks' column contains the UniProt annotated crosslinks.</li>
                    <li className="text-justify">The 'Binding Region' column contains the Binding region from IntAct.
                    The data is structured as follows: UniProt accession number of interactor A (binding region range),
                    UniProt accession number of interactor B (binding region range), interaction type, and MI score.
                    For instance: O00459(291-306)_P46108(124-198)_direct interaction(MI SCORE:0.4). </li>
                    <li className="text-justify">The 'InterPro' column contains the InterPro annotated domains.</li>
                    <li className="text-justify">
                      The 'amMean' column shows the average AlphaMissense pathogenicity score
                      across all 19 possible amino acid substitutions at that position. Values range from 0 to 1,
                      where higher values indicate greater intolerance to mutations
                      (≥0.564 = likely pathogenic, ≤0.34 = likely benign).
                    </li>
                    <li className="text-justify">
                      The 'amClass' column shows the residue-level classification derived from
                      the mean score: <em>likely_pathogenic</em> (mean ≥ 0.564), <em>ambiguous</em> (0.34–0.564),
                      or <em>likely_benign</em> (mean ≤ 0.34), following the thresholds from
                      Cheng et al., Science 2023.
                    </li>
                    <li className="text-justify">
                      The 'amMax' column shows the highest AlphaMissense score among all 19
                      possible substitutions at that position — the worst-case mutation scenario.
                      A high value indicates that at least one substitution is strongly predicted as pathogenic.
                    </li>
                    <li className="text-justify">
                      The 'amMin' column shows the lowest AlphaMissense score among all 19
                      possible substitutions at that position — the best-case mutation scenario.
                      A high minimum value suggests the position is intolerant to any mutation.
                    </li>
                    <li className="text-justify">
                      The 'amPathogenicFrac' column shows the fraction of the 19 possible
                      substitutions classified as pathogenic (score ≥ 0.564). A value of 1.0 means every
                      possible mutation is predicted pathogenic; 0.0 means none are. This reflects the
                      overall mutational constraint at that position.
                    </li>
                    </ul>

                     <GraphlqlQuery type={type} target={target} tab={activeTabIndex}/>

                    <p className="m-0 mb-1 text-justify">For more information, please refer to the
                     <Link className="" to="/about/" target="_blank"><b> about</b></Link> or <Link className="" to="/api/" target="_blank"><b> API</b></Link> page.
                     </p>
                </Dialog>
                <Tooltip target=".export-buttons>button" position="bottom" />
                { loadFunctionalInfo &&  (
                 <div className="row mb-4">
                    <ProgressSpinner style={{width: '50px', height: '50px'}}
                    strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                 </div>
                )}
              {(!loadFunctionalInfo) && (
                 <ResiduesTable ResidueData={data} annotationMap={annotationMap} />
               )}
            </div>
        </div>
        {(!loadFunctionalInfo && (type === 'gene' || type === 'protein')) && (
          <div className="row mb-5">
              <h2 className="display-6 fw-bold mb-4">Feature Viewer</h2>
                 <div className="col-12">
                   <MemoizedFeaturesViewer data={data} colors={colors}  domainInterPro={domainInterPro}/>
                  </div>
             </div>
          )}
         {(!loadFunctionalInfo) && (
            <div className="row mb-5">
              <h2 className="display-6 fw-bold mb-4">Structure</h2>
              <div className="col-md-3 mb-3">
                <div className="bg-light rounded-3">
                    <div className="px-4 py-4">
                     {hasMultimer && (
                    <div className="flex justify-content-center mb-3">
                        <label htmlFor="structure-color" className="form-label me-2">
                            Structure Type
                        </label>
                                <SelectButton
                                    value={valueModelType}
                                    onChange={(e) => {
                                        if (e.value) {
                                            setValueModelType(e.value);
                                            setSuperpose(false);
                                            setAnnotation("pLDDT");
                                        }
                                    }}
                                    options={optionsModelType}
                                    optionLabel="label"
                                    optionValue="value"
                                    className="fs--1"
                                    style={{ display: 'flex', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}
                                />
                            </div>
                    )}
                        {(valueModelType === 'Multimer' && structureOptions.length > 0) && (
                            <div className="mb-2">
                                <label htmlFor="structure-complex" className="form-label">Complex structure</label>
                                <Dropdown
                                    inputId="structure-complex"
                                    value={selectedStructureFile}
                                    options={structureDropdownOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => { setSelectedStructureFile(e.value); setSuperpose(false); }}
                                    filter={structureDropdownOptions.length > 8}
                                    style={{ width: '100%' }}
                                    className="structure-dropdown outline-none fs--2 mb-2"
                                    placeholder="Select a structure"
                                />
                            </div>
                        )}
                        <div className="mb-2">
                            <label
                                    htmlFor="structure-color"
                                    className="form-label"
                            >
                                Annotation 
                            </label>
                            <select
                                    className="form-select mb-2 outline-none"
                                    name="structure-color"
                                    value={annotation}
                                    onChange={handleAnnotation}
                            >
                                <option value="None">None</option>
                                <option value="pLDDT">pLDDT</option>
                                <option value="amMean">amMean</option>
                                <option value="AlphaFill" disabled={valueModelType === "Multimer"}>AlphaFill</option>
                                <option value="surface">Accessible Surface Area</option>
                                <option value="hydrophobicity">Hydrophobicity</option>
                                <option value="name">Residue Name</option>
                                <option value="rainbow">Rainbow</option>
                                <option value="gaussian" disabled={valueModelType === "Multimer"}>Molecular Surface</option>
                                 <option value="unAnnNatVariants"
                                 disabled={data?.every(residue => !residue.unAnnNatVariants || residue.unAnnNatVariants.trim() === "-")}
                                 >Natural variants</option>
                                 <option value="unMoleculeProcessing"
                                 disabled={data?.every(residue => !residue.unMoleculeProcessing || residue.unMoleculeProcessing.trim() === "-")}
                                 >Molecule processing</option>
                                 <option value="unLocation"
                                 disabled={data?.every(residue => !residue.unLocation || residue.unLocation.trim() === "-")}
                                 >Location</option>
                                 <option
                                 value="unMotives"
                                 disabled={data?.every(residue => !residue.unMotives || residue.unMotives.trim() === "-")}>Motives</option>
                                 <option value="unDomains"
                                 disabled={data?.every(residue => !residue.unDomains || residue.unDomains.trim() === "-")}
                                 >Domains</option>
                                 <option value="interPro" disabled={!domainInterPro || domainInterPro.length === 0 }>InterPro</option>
                                 <option value="pfamDomainPosHmm"
                                 disabled={data?.every(residue => !residue.pfamDomainPosHmm || residue.pfamDomainPosHmm.trim() === "-")}
                                 >PFAM-HMM Pos</option>
                                 <option value="unLigandsBindingSite"
                                 disabled={data?.every(residue => !residue.unLigandsBindingSite || residue.unLigandsBindingSite.trim() === "-")}
                                 >Binding site</option>
                                 <option value="unAnnPropActiveSite"
                                 disabled={data?.every(residue => !residue.unAnnPropActiveSite || residue.unAnnPropActiveSite.trim() === "-")}
                                 >Active site</option>
                                 <option value="phosPtms"
                                 disabled={data?.every(residue => !residue.phosPtms || residue.phosPtms.trim() === "-")}
                                 >PTMs</option>
                                 <option value="unGlycosylations"
                                  disabled={data?.every(residue => !residue.unGlycosylations || residue.unGlycosylations.trim() === "-")}
                                 >Glycosylations</option>
                                 <option value="unLipidations"
                                 disabled={data?.every(residue => !residue.unLipidations || residue.unLipidations.trim() === "-")}
                                 >Lipidations</option>
                                 <option value="unModifiedRes"
                                 disabled={data?.every(residue => !residue.unModifiedRes || residue.unModifiedRes.trim() === "-")}
                                 >Modified Residue</option>
                                 <option value="unDisulfideBongBridges"
                                 disabled={data?.every(residue => !residue.unDisulfideBongBridges || residue.unDisulfideBongBridges.trim() === "-")}
                                 >Disulfide bond bridges</option>
                                 <option value="unSecondaryStructure"
                                  disabled={data?.every(residue => !residue.unSecondaryStructure || residue.unSecondaryStructure.trim() === "-")}
                                 >Secondary Structure</option>
                                 <option value="afPredictedSecondaryStructure">AF-Predicted secondary structure</option>
                                 <option value="unCrossLinks"
                                  disabled={data?.every(residue => !residue.unCrossLinks || residue.unCrossLinks.trim() === "-")}
                                 >CrossLinks</option>
                                 <option value="inBindingRegion"
                                  disabled={data?.every(residue => !residue.inBindingRegion || residue.inBindingRegion.trim() === "-")}
                                 >Binding Region</option>
                            </select>

                            { (data && annotation) && (
                              <FunctionalAnnotationStructureLegend annotation={annotation}  colors={colors}  domainInterPro={domainInterPro}/>
                            )}
                            {(!annotations.includes(annotation) && valueModelType === "Monomer" )&& (
                             <div className="mb-2">
                            <label htmlFor="structure" className="form-label">PDB </label>
                            <select
                              className="form-select mb-3"
                              name="structure-variant"
                              value={structurePDBId}
                              disabled={pdbList.length <= 1}
                              onChange={handlePDBid}>
                              {pdbList.map((pdbIdItem, index) => {
                                const validPdbId = typeof pdbIdItem === 'string' ? pdbIdItem.toUpperCase().trim() : String(pdbIdItem || "").toUpperCase().trim();
                                return (
                                  <option key={index} value={validPdbId} >
                                    {validPdbId === "" ? (pdbList.length <=1 ? "N/A" : "Select a PDB") : validPdbId}
                                  </option>
                                );
                              })}
                            </select>
                            <Button label="Superposition" icon="pi pi-clone" className="fs--2 mb-1 me-2"
                                onClick={() => setSuperpose(true)} disabled={pdbList.length <= 1}/>

                            <Button label="Reset" icon="pi pi-refresh" className="fs--2 mb-1"
                                onClick={handleReset} disabled={pdbList.length <= 1}/>
                             </div>
                             )}

                            {(data && annotation === "AlphaFill" && ligandsOption.length > 0) && (
                             <div className="mb-2">
                               <label htmlFor="filters" className="form-label">Ligand </label>
                               <ListBox multiple value={selectedLigand} onChange={(e) => setSelectedLigand(e.value)}
                                options={ligandsOption}
                                className="w-full md:w-14rem fs--2 text-justify list-filter mb-2" listStyle={{ maxHeight: '100px'}}/>

                                 <Button label="Unselect" icon="pi pi pi-refresh" className="fs--2 mb-1"
                                onClick={filterLigandsReset} />

                          </div>
                           )}
                           {( data && annotation === "AlphaFill" && ligandsOption.length === 0) && (
                             <div className="mb-2">
                               <label htmlFor="filters" className="form-label">Ligand </label>
                               <p className="fs--2 text-justify"> No results found</p>
                             </div>
                            )}

                            <p className="fs--2 text-justify">
                                Click on the viewer plugin icons to access more
                                advanced features from
                                <Link target="_blank" to="https://molstar.org/viewer-docs/">
                                <b> Mol*</b>
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md-9">
                <div className="viewer-box">
                    {data && uniprotNumber && (
                        <MolstarViewerFunctional
                            database="AF"
                            dataResidue={dataForMolstar}
                            annotation={annotation}
                            colors={colors}
                            ligands={selectedLigand}
                            setLigandsOption={setLigandsOption}
                            domainInterPro={domainInterPro}
                            superpose={superpose}
                            pdbId={structurePDBId}
                            multimerLink={multimerLink}
                            modelType={valueModelType}
                        />
                    )}
                </div>
            </div>
            </div>
            )}
            {(!loadFunctionalInfo && annotation === "inBindingRegion") && (
               <div className="row mb-5">
               <h2 className="display-6 fw-bold mb-4">Network</h2>
               <div className="col-md-2">
                <div className="bg-light rounded-3">
                    <div className="px-4 py-4">
                        <div className="mb-2">
                            <label
                               htmlFor="structure-color"
                              className="form-label" >
                                Viewer Tools 
                            </label>
                  <div className="mb-1 g-3">
                   <Button label="" tooltip="Export as SVG" icon="pi pi-download" className="fs--2 me-1"
                               onClick={handleExportSVG}/>
                    <Button label="" tooltip="Export as PNG" icon="pi pi-camera" className="fs--2"
                               onClick={handleExportPNG}/>
                   </div>
                   </div>
                  </div>
                </div>
              </div>
                <div className="col-md-7">
                   <NetworkDiagram  dataResidue={data} annotation={annotation} svgRef={svgRef} />
               </div>
                <div className="col-md-3">
                <Legend />
              </div>
               </div>
            )}
</>
  )
}
export default ResidueSection;